import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import { useGenerateAISuggestion } from '../../hooks/useGenerateAISuggestion';
import { ExceptionEntry } from '../../types';

const useStyles = makeStyles(theme => ({
  generatorCard: {
    border: `1px dashed ${theme.palette.type === 'dark' ? '#4a5568' : '#cbd5e0'}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? '#1a202c' : '#f7fafc',
    marginBottom: theme.spacing(3),
  },
  generatorContent: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  aiIcon: {
    fontSize: '2rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  generateButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  errorAlert: {
    marginTop: theme.spacing(2),
  },
}));

interface AISuggestionGeneratorProps {
  eventId: string;
  entries?: any[];
  onSuggestionGenerated: () => void;
}

export const AISuggestionGenerator: React.FC<AISuggestionGeneratorProps> = ({
  eventId,
  entries,
  onSuggestionGenerated,
}) => {
  const classes = useStyles();
  const [hasAttempted, setHasAttempted] = useState(false);
  const { isGenerating, error, generate } = useGenerateAISuggestion();

  const extractErrorPrompt = (): string => {
    if (!entries) return 'Unknown error occurred';

    const exceptionEntry = entries.find(
      entry => entry.type === 'exception'
    ) as ExceptionEntry | undefined;
    
    if (!exceptionEntry) return 'Unknown error occurred';

    // Create a comprehensive prompt from the exception data
    const exceptions = exceptionEntry.data.values;
    const mainException = exceptions[0]; // Get the primary exception
    
    if (!mainException) return 'Unknown error occurred';

    // Build the prompt with exception type, value, and relevant stack trace info
    let prompt = `Exception: ${mainException.type}: ${mainException.value}`;
    
    // Add some context from the stack trace if available
    if (mainException.stacktrace?.frames?.length > 0) {
      const relevantFrames = mainException.stacktrace.frames
        .filter(frame => frame.inApp) // Focus on application code
        .slice(0, 3); // Get top 3 relevant frames
      
      if (relevantFrames.length > 0) {
        prompt += '\n\nStack trace context:\n';
        relevantFrames.forEach(frame => {
          prompt += `- ${frame.filename} in ${frame.function} at line ${frame.lineNo}\n`;
        });
      }
    }

    return prompt;
  };

  const handleGenerate = async () => {
    setHasAttempted(true);
    const prompt = extractErrorPrompt();
    
    try {
      await generate(prompt, eventId);
      onSuggestionGenerated(); // Notify parent to refresh
    } catch (err) {
      console.error('Failed to generate AI suggestion:', err);
    }
  };

  return (
    <Card className={classes.generatorCard} variant="outlined">
      <CardContent className={classes.generatorContent}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Get AI-powered insights and potential solutions for this error
        </Typography>

        {isGenerating ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              Analyzing error with AI...
            </Typography>
          </Box>
        ) : (
          <Button
            variant="contained"
            className={classes.generateButton}
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            Generate AI Suggestion
          </Button>
        )}

        {error && hasAttempted && (
          <Box className={classes.errorAlert}>
            <Typography variant="body2" color="error">
              Failed to generate AI suggestion: {error.message}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};